/**
 * Login Bridge Page Component
 * 
 * Shows the login page with Auth0 button instead of redirecting immediately.
 * This allows users to see the login page after declining Auth0 consent.
 * Public page (no authentication required).
 */

import React, { useEffect, useState } from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import { getQueryParam } from "../utils/urlParams";
import { login } from "../services/authApi";
import "../components/auth/loginPage.css";

export function LoginBridgePage(): JSX.Element {
  const [showRateLimitWarning, setShowRateLimitWarning] = useState(false);
  const [showAccessDeniedWarning, setShowAccessDeniedWarning] = useState(false);
  
  useEffect(() => {
    // Check if we're coming from error page
    const urlParams = new URLSearchParams(window.location.search);
    const fromError = urlParams.get("from_error");
    const errorType = urlParams.get("error_type");
    
    if (fromError === "true") {
      if (errorType === "rate_limit") {
        setShowRateLimitWarning(true);
      } else if (errorType === "access_denied") {
        setShowAccessDeniedWarning(true);
      }
      // Clean up URL to remove error params AFTER setting state
      // This ensures apiClient interceptor can check from_error before it's removed
      // Use setTimeout to ensure state is set before URL cleanup
      setTimeout(() => {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
      }, 100);
    }
  }, []);
  
  const handleLogin = () => {
    // Get returnTo from query params
    // Default to "/" (home page) instead of "/app" which doesn't exist
    const returnTo = getQueryParam("returnTo") || "/";
    
    // Redirect to Auth0 login via backend
    login(returnTo);
  };

  return (
    <PublicLayout>
      <div className="login-page">
        <div className="login-page__container">
          <div className="login-page__brand">
            <img
              src="/assets/zakyam-logo.png"
              alt="Zakyam AI Trader"
              className="login-page__logo"
            />
            <h1 className="login-page__title">Zakyam AI Trader</h1>
            <p className="login-page__subtitle">
              Autonomous AI execution, news intelligence, and capital oversight
            </p>
          </div>
          
          <div className="login-page__content">
            <h2 className="login-page__heading">Welcome</h2>
            <p className="login-page__description">
              Sign in to access your trading dashboard
            </p>
            
            {showRateLimitWarning && (
              <div style={{
                padding: "1rem",
                marginBottom: "1.5rem",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "0.5rem",
                color: "#856404"
              }}>
                <strong>Rate Limit Notice:</strong> You've made too many login attempts. Please wait a few minutes before trying again.
              </div>
            )}
            
            {showAccessDeniedWarning && (
              <div style={{
                padding: "1rem",
                marginBottom: "1.5rem",
                backgroundColor: "#f8d7da",
                border: "1px solid #dc3545",
                borderRadius: "0.5rem",
                color: "#721c24"
              }}>
                <strong>Authorization Declined:</strong> You previously declined to authorize the application. 
                <br />
                <br />
                <strong>To proceed, you have two options:</strong>
                <br />
                <br />
                1. <strong>Accept authorization:</strong> Click "Sign In with Auth0" below and make sure to click "Accept" on the authorization screen. 
                If you decline again, you will be redirected back here.
                <br />
                <br />
                2. <strong>Use a different account:</strong> Click "Sign In with Auth0" below. The login page will appear, allowing you to log in with a different account or create a new one.
              </div>
            )}
            
            <button
              type="button"
              className="login-page__button"
              onClick={handleLogin}
              disabled={showRateLimitWarning}
              style={showRateLimitWarning ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            >
              Sign In with Auth0
            </button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default LoginBridgePage;

